// components/slides/SlidesToolbar.tsx
'use client';

// Studio-look toolbar for the Slides canvas — TOOLS ONLY, with Studio's
// NewInpaintToolbar button visual copied verbatim (#1B1B1B pill; selected =
// #0D0D0D on #ECECEC, hover = white on #2A2A2A, idle = #B4B4B4). Laid out
// HORIZONTALLY and pinned bottom-center of the stage, like Figma Slides —
// tooltips open upward. One uninterrupted icon row: no separators, no
// per-button colors. Non-tool actions (present, export, agent) live elsewhere
// (top chrome / chat). Every element type the document supports has a tool
// here — ellipse and image used to be agent-only.
// Tools drive slidesStore runtime.activeTool.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  Image as ImageIcon,
  LibraryBig,
  MessageSquarePlus,
  MousePointer2,
  MoveUpRight,
  Square,
  Stamp,
  Type,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SlideBlockKind, SlidesTool } from '#types/types';
import { BLOCK_KINDS, BLOCK_LIBRARY, STAMP_GROUP_LABELS, initialsOf, type StampGroup } from '#lib/slides/blockArtwork';
import { useSlidesStore } from './slidesStore';
import { useSlidesAuthor } from './useSlidesAuthor';

type IconType = React.ComponentType<{ color?: string; size?: number; className?: string }>;

/**
 * Visual copy of NewInpaintToolbar's internal ToolbarButton. NOT the shared
 * ui/SidebarButton: the vertical canvas toolbar needs the #808080/#000000 idle
 * + white/#202020 hover + white/#3A3A3A selected states that SidebarButton
 * can't express. Every button uses these identical states — no custom colors.
 */
const stopEvent = (e: React.SyntheticEvent) => e.stopPropagation();

const ToolbarButton = React.memo(function ToolbarButton({
  icon: Icon,
  tooltipText,
  tool,
  selected = false,
  onSelect,
}: {
  icon: IconType;
  tooltipText: string;
  /** Stable hook for the E2E harness (tooltips render in a portal, so there is
   *  no accessible text on the button itself to select by). */
  tool: SlidesTool | 'library';
  selected?: boolean;
  /** Takes the tool rather than a bound closure, so the parent can pass one
   *  stable callback to all eight buttons and React.memo actually holds. */
  onSelect: (tool: SlidesTool | 'library') => void;
}) {
  const [isHovered, setIsHovered] = useState(false);

  let iconColor: string;
  let backgroundColor: string;
  if (selected) {
    // Studio's tool rail (ChatGPT's): the chosen tool a near-white circle, never the blue accent,
    // which would pull the eye off the sheet under review.
    iconColor = '#0D0D0D';
    backgroundColor = '#ECECEC';
  } else if (isHovered) {
    iconColor = '#FFFFFF';
    backgroundColor = '#2A2A2A';
  } else {
    iconColor = '#B4B4B4';
    backgroundColor = 'transparent';
  }

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(tool);
    },
    [onSelect, tool],
  );
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const style = useMemo(
    () => ({ borderRadius: '9999px', cursor: 'pointer', backgroundColor, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
    [backgroundColor],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-slides-tool={tool}
          aria-label={tooltipText}
          onClick={handleClick}
          onMouseDown={stopEvent}
          onTouchStart={stopEvent}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="transition-colors duration-150 ease-in-out"
          style={style}
        >
          <Icon color={iconColor} size={18} />
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
});

const TOOLS: { tool: SlidesTool; icon: IconType; tooltipText: string }[] = [
  { tool: 'select', icon: MousePointer2, tooltipText: 'Select (V)' },
  { tool: 'text', icon: Type, tooltipText: 'Text' },
  { tool: 'note', icon: MessageSquarePlus, tooltipText: 'Comment' },
  { tool: 'arrow', icon: MoveUpRight, tooltipText: 'Arrow' },
  { tool: 'rect', icon: Square, tooltipText: 'Rectangle' },
  { tool: 'ellipse', icon: Circle, tooltipText: 'Ellipse' },
  { tool: 'image', icon: ImageIcon, tooltipText: 'Image' },
  { tool: 'stamp', icon: Stamp, tooltipText: 'Stamp' },
];

/** Thibaut's stamp library (2026-10-05): pick a stamp and it lands on the page, ready to move and edit. */
/** A drop-up that closes on an outside click or Escape. */
const useDropUp = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape); };
  }, [open]);
  return { open, setOpen, ref };
};

const MENU_CLASS = 'absolute bottom-12 left-1/2 z-[9999] w-64 -translate-x-1/2 rounded-[20px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-[0_18px_52px_rgba(0,0,0,0.5)]';
const ITEM_CLASS = 'flex w-full flex-col items-start gap-0.5 rounded-[12px] px-3 py-2 text-left transition-colors hover:bg-white/[0.08] focus:bg-white/[0.08] focus:outline-none';

/** The whole tool row folded into one button showing the current tool, for a narrow stage. */
const ToolPicker = ({ activeTool, onSelect }: { activeTool: SlidesTool; onSelect: (tool: SlidesTool) => void }) => {
  const { open, setOpen, ref } = useDropUp();
  const current = TOOLS.find((t) => t.tool === activeTool) ?? TOOLS[0]!;
  return (
    <div ref={ref} className="relative" onMouseDown={stopEvent}>
      <ToolbarButton icon={current.icon} tooltipText="Tools" tool={current.tool} selected={open} onSelect={() => setOpen((v) => !v)} />
      {open && (
        <div role="menu" aria-label="Tools" className={MENU_CLASS} style={{ width: 200 }}>
          {TOOLS.map(({ tool, icon: Icon, tooltipText }) => (
            <button key={tool} type="button" role="menuitemradio" aria-checked={tool === activeTool}
              className={`${ITEM_CLASS} !flex-row !items-center gap-2.5`} onClick={() => { onSelect(tool); setOpen(false); }}>
              <Icon size={16} color={tool === activeTool ? '#FFFFFF' : '#B4B4B4'} />
              <span className={tool === activeTool ? 'text-white' : 'text-[#ECECEC]'} style={{ fontSize: 14, lineHeight: '20px' }}>{tooltipText}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StampLibrary = ({ projectId: pid }: { projectId: string }) => {
  const { open, setOpen, ref } = useDropUp();
  const initials = initialsOf(useSlidesAuthor()?.name);
  const hasCover = useSlidesStore((s) => s.decks[pid]?.doc.pages.some((p) => p.cover) ?? false);
  const run = (action: () => void) => { action(); setOpen(false); };
  const place = (kind: SlideBlockKind) => run(() => {
    const st = useSlidesStore.getState(), pageId = st.runtime[pid]?.activePageId ?? st.decks[pid]?.doc.pages[0]?.id;
    if (pageId) st.addLibraryStamp(pid, pageId, kind, initials);
  });
  const item = ITEM_CLASS;
  return (
    <div ref={ref} className="relative" onMouseDown={stopEvent}>
      <ToolbarButton icon={LibraryBig} tooltipText="Stamp library" tool="library" selected={open} onSelect={() => setOpen((v) => !v)} />
      {open && (
        <div role="menu" aria-label="Stamp library" className={MENU_CLASS}>
          {!hasCover && (
            <button type="button" role="menuitem" className={item} onClick={() => run(() => {
              useSlidesStore.getState().ensureCoverPage(pid, initials);
              const st = useSlidesStore.getState(), cover = st.decks[pid]?.doc.pages[0];
              if (cover) st.focusPage(pid, cover.id);
            })}>
              <span className="text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>Add review cover page</span>
              <span className="text-[#B4B4B4]" style={{ fontSize: 12, lineHeight: '16px' }}>Stamp, Better Places, Tenant Survey and cartouche</span>
            </button>
          )}
          {(Object.keys(STAMP_GROUP_LABELS) as StampGroup[]).map((group) => (
            <div key={group} role="group" aria-label={STAMP_GROUP_LABELS[group]}>
              <div className="px-3 pb-1 pt-2 text-[#8E8E8E]" style={{ fontSize: 12 }}>{STAMP_GROUP_LABELS[group]}</div>
              {BLOCK_KINDS.filter((kind) => BLOCK_LIBRARY[kind].group === group).map((kind) => (
                <button key={kind} type="button" role="menuitem" className={item} onClick={() => place(kind)}>
                  <span className="text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{BLOCK_LIBRARY[kind].label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
};

interface SlidesToolbarProps {
  /** The deck's container project id — NOT the deck DTO: the toolbar reads
   *  nothing but the tool, and taking the document would re-render it on every
   *  keystroke that bumps the deck. Same contract as SlidesZoomBar. */
  projectId: string;
}

const SlidesToolbar = ({ projectId: pid }: SlidesToolbarProps) => {
  const activeTool: SlidesTool = useSlidesStore((s) => s.runtime[pid]?.activeTool) ?? 'select';
  const patchRuntime = useSlidesStore((s) => s.patchRuntime);

  const setTool = useCallback(
    (tool: SlidesTool | 'library') => { if (tool !== 'library') patchRuntime(pid, { activeTool: tool }); },
    [patchRuntime, pid],
  );

  return (
    <TooltipProvider>
      <div
        className="flex flex-row gap-0.5 rounded-full border border-[#262626] bg-[#1B1B1B] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)] pointer-events-auto"
        style={CONTAINER_STYLE}
      >
        {/* The tools are the least used control here: on a narrow stage (both side panels open) they
            fold into one button first, so they never slide under the zoom island. */}
        <div className="flex flex-row gap-0.5 @max-[719px]/stage:hidden">
          {TOOLS.map(({ tool, icon, tooltipText }) => (
            <ToolbarButton
              key={tool}
              icon={icon}
              tooltipText={tooltipText}
              tool={tool}
              selected={activeTool === tool}
              onSelect={setTool}
            />
          ))}
        </div>
        <div className="hidden @max-[719px]/stage:block">
          <ToolPicker activeTool={activeTool} onSelect={setTool} />
        </div>
        <StampLibrary projectId={pid} />
      </div>
    </TooltipProvider>
  );
};

export default SlidesToolbar;
